import { atom as y, map as S, computed as v } from "nanostores";
import { copilotTools as E, copilotInstructions as F, copilotContext as I, uniqueId as x } from "./global-state.js";
function w() {
  return Math.floor(Date.now() / 1e3);
}
const T = {
  idle: ["initializing", "ending"],
  initializing: ["ready", "error"],
  ready: ["sending", "ending"],
  sending: ["receiving"],
  receiving: ["ready"],
  ending: ["ended", "error"],
  error: ["initializing", "ending"],
  ended: []
  // Can only transition to itself (terminal state)
};
function j(o, e) {
  if (o === e)
    return o;
  if (!T[o])
    throw new Error(`Unknown status: ${o}`);
  if (T[o].includes(e))
    return e;
  throw new Error(`Invalid status transition: ${o} -> ${e}`);
}
class G {
  constructor(e) {
    this.config = {
      includeGlobalContext: !0,
      includeGlobalTools: !0,
      includeGlobalInstructions: !0
    }, this.__status = y("idle"), this.__messages = y([]), this.__meta = y({}), this.__uploadFiles = y([]), this.__queuedMessages = y([]), this.__abortController = null, this.__enableWebSearch = y(!0), this.__serverSideTools = S({}), this.__sessionInstructions = S({}), this.__sessionTools = S({}), this.__sessionContext = S({}), this.__tools = v(
      [this.__serverSideTools, E, this.__sessionTools],
      (t, n, a) => {
        const l = {}, s = {
          ...this.config.includeGlobalTools === !0 ? n : {},
          ...a
        };
        for (const i of Object.values(t))
          if (i != null) {
            const u = s[i.name];
            l[i.name] = {
              ...u?.type === "server" ? u : void 0,
              ...i
            };
          }
        for (const i of Object.values(s))
          i != null && i.type === "client" && l[i.name] == null && (l[i.name] = i);
        return l;
      }
    ), this.__statefulMessages = v([this.__messages, this.__tools], U), this.__instructions = v(
      [F, this.__sessionInstructions],
      (t, n) => ({
        ...this.config.includeGlobalInstructions === !0 ? t : {},
        ...n
      })
    ), this.__context = v([I, this.__sessionContext], (t, n) => ({
      ...this.config.includeGlobalContext === !0 ? t : {},
      ...n
    })), this.__suggestionsRefreshTrigger = y([]), e && (this.config = { ...this.config, ...e });
  }
  /**
   * The current status of the Copilot session.
   */
  get status() {
    return this.__status.get();
  }
  /**
   * If the session is being ended.
   */
  get isEnding() {
    return this.status === "ending";
  }
  get isEnded() {
    return this.status === "ended";
  }
  /**
   * Sets the status of the Copilot session.
   */
  setStatus(e) {
    this.__status.set(j(this.status, e));
  }
  /**
   * adds an instruction just for this session
   */
  addInstruction(e) {
    const t = x();
    return this.__sessionInstructions.setKey(t, e), () => {
      this.__sessionInstructions.setKey(t, void 0);
    };
  }
  /**
   * Adds a tool just for this session
   */
  addTool(e) {
    return this.__sessionTools.setKey(e.name, e), () => {
      this.__sessionTools.setKey(e.name, void 0);
    };
  }
  /**
   * Adds a context just for this session
   */
  addContext(e) {
    const t = x();
    return this.__sessionContext.setKey(t, e), () => {
      this.__sessionContext.setKey(t, void 0);
    };
  }
  /**
   * Sets the meta data for this session
   */
  setSessionMeta(e) {
    this.__meta.set(e);
  }
  /**
   * Gets the meta data for this session
   */
  getSessionMeta() {
    return this.__meta.get();
  }
  /**
   * Sets whether the model can search the web to answer questions.
   */
  setWebSearchEnabled(e) {
    this.__enableWebSearch.set(e);
  }
  /**
   * Gets whether web search is enabled for this session.
   */
  getWebSearchEnabled() {
    return this.__enableWebSearch.get();
  }
  /**
   * Updates the meta data for a specific message by targeting message.meta.id
   */
  setMessageMeta(e, t) {
    const a = this.__messages.get().map((l) => l.role === "model" && l.type === "meta" && "meta" in l && l.meta?.id === e ? {
      ...l,
      meta: t
    } : l);
    this.__messages.set(a);
  }
  /**
   * Clears any messages that are queued to be sent after the current response completes.
   * Returns the messages that were cleared so they can be restored if needed.
   */
  clearQueuedMessages() {
    const e = this.__queuedMessages.get();
    return this.__queuedMessages.set([]), e;
  }
  /**
   * Stops the current in-flight exchange.
   *
   * Aborts the streaming HTTP request immediately (so the UI becomes responsive
   * right away) and fires a request to cancel the Temporal workflow on the
   * backend (so it stops burning LLM tokens).
   *
   * Always returns `{ messages }` — the messages that were queued while the
   * exchange was running — so the caller can restore them to the input box
   * regardless of whether the backend call succeeded. If the backend call
   * fails, `error` is also set so the caller can surface it to the user.
   *
   * Never throws. Safe to call in any state.
   */
  async stop() {
    if (this.status !== "sending" && this.status !== "receiving")
      return { messages: [] };
    this.__abortController?.abort();
    const e = this.clearQueuedMessages(), t = window.__hydra_copilot__?.stopExchange;
    if (t != null && this.id != null)
      try {
        await t({ id: this.id });
      } catch (n) {
        return {
          messages: e,
          error: n instanceof Error ? n : new Error(String(n))
        };
      }
    return { messages: e };
  }
  /**
   * Forces a refresh of suggestions for this session.
   * This will trigger any active suggestion hooks to regenerate their suggestions.
   * @param instruction - Custom instructions to include in the next suggestions fetch.
   * These instructions will be used once and then cleared.
   */
  refreshSuggestions(e) {
    this.__suggestionsRefreshTrigger.set([{ instruction: e }]);
  }
  /**
   * Initializes a new session, either from an existing session id,
   * or by creating a new one
   */
  async initialize(e) {
    if (this.status !== "idle" && this.status !== "error")
      return this.id;
    this.setStatus("initializing");
    try {
      const t = window.__hydra_copilot__?.startSession;
      if (t == null)
        throw new Error("Copilot session not configured. Please set the Copilot configuration first.");
      if (e == null) {
        const n = await t();
        if (n.id == null)
          throw new Error("Failed to start session, no session ID returned");
        this.id = n.id, this.__serverSideTools.set(C(n.tools ?? []));
      } else {
        this.id = e;
        const n = await this.loadSession();
        this.__messages.set(n.messages ?? []), this.__meta.set(n.meta ?? {}), this.__serverSideTools.set(C(n.tools ?? []));
      }
      return this.setStatus("ready"), this.id;
    } catch (t) {
      throw this.setStatus("error"), t;
    }
  }
  /**
   * Ends the current session, clearing all messages and state.
   */
  async end() {
    this.setStatus("ending");
    try {
      const e = window.__hydra_copilot__?.endSession;
      if (e == null)
        throw new Error("Copilot session not configured. Please set the Copilot configuration first.");
      this.id != null && await e(this.id), this.__messages.set([]), this.__queuedMessages.set([]), this.id = void 0, this.__sessionInstructions.set({}), this.__sessionTools.set({}), this.__sessionContext.set({}), this.__serverSideTools.set({}), this.__meta.set({}), this.setStatus("ended");
    } catch (e) {
      throw this.setStatus("error"), e;
    }
  }
  /**
   * Returns pending approval tool calls from the session's messages.
   * A pending approval is a tool call with `needs_approval === true` that
   * doesn't yet have a corresponding tool_response message.
   */
  getPendingApprovals() {
    const e = this.__messages.get(), t = /* @__PURE__ */ new Set();
    for (const a of e)
      if (a.role === "user" && a.type === "tool_response")
        for (const l of a.tool_responses)
          t.add(l.tool_call_id);
    const n = [];
    for (const a of e)
      if (a.role === "model" && a.type === "tool_call")
        for (const l of a.tool_calls)
          l.needs_approval === !0 && !t.has(l.id) && n.push({ id: l.id, name: l.name });
    return n;
  }
  async sendMessage(e) {
    let t = typeof e == "string" ? [{ role: "user", type: "text", text: e, timestamp: w() }] : [...e];
    const n = window.__hydra_copilot__?.sendStreamingMessage;
    if (n == null)
      throw new Error("Copilot session not configured. Please set the Copilot configuration first.");
    if (this.status === "idle" && await this.initialize(), this.status !== "ready") {
      if (this.status === "sending" || this.status === "receiving") {
        this.__queuedMessages.set([...this.__queuedMessages.get(), ...t]);
        return;
      }
      throw new Error(`Cannot send message while session is in "${this.status}" state.`);
    }
    if (!t.some((r) => r.type === "tool_response")) {
      const r = this.getPendingApprovals();
      r.length > 0 && (t = [O(r), ...t]);
    }
    let l = null;
    try {
      this.setStatus("sending"), this.__abortController = new AbortController();
      let r = t.map((u) => ({
        ...u,
        timestamp: u.timestamp || w()
      })), s = 0;
      for (; r.length > 0; ) {
        this.__messages.set([...this.__messages.get(), ...r]);
        const u = [], h = (g) => {
          if (this.setStatus("receiving"), s += 1, this.__messages.set([...this.__messages.get(), g]), g.role === "model" && g.type == "tool_call")
            for (const d of g.tool_calls) {
              if (d.needs_approval || d.is_client_tool !== !0)
                continue;
              const f = this.__tools.get()[d.name];
              u.push(
                (async () => {
                  try {
                    if (f?.type === "server")
                      throw new Error(
                        `Tool ${d.name} is a server-side tool and cannot be called on the client.`
                      );
                    if (f?.handler == null)
                      throw new Error(`Tool ${d.name} does not have a handler defined.`);
                    const m = await f.handler({ args: d.args, session: this, toolCallId: d.id }) ?? null;
                    return {
                      tool_call_id: d.id,
                      name: d.name,
                      response: { result: m, error: null },
                      finished: !0
                    };
                  } catch (m) {
                    return {
                      tool_call_id: d.id,
                      name: d.name,
                      response: {
                        result: null,
                        error: `Error calling tool ${d.name}: ${m instanceof Error ? m.message : String(m)}`
                      },
                      finished: !0
                    };
                  }
                })()
              );
            }
          if (g.role === "user" && g.type === "tool_response")
            for (const d of g.tool_responses) {
              const f = this.__tools.get()[d.name], m = P(
                [...this.__messages.get(), ...r],
                d.tool_call_id
              );
              f?.type === "server" && f?.handler != null && d.finished === !0 && f.handler({
                args: m?.args || {},
                session: this,
                response: d.response,
                toolCallId: d.tool_call_id,
                wasApproved: d.was_approved === !0
              });
            }
        }, c = {
          id: this.id,
          messages: r,
          tools: A(this.__tools.get()),
          instructions: R(this.__instructions.get()),
          context: q(this.__context.get()),
          onMessage: h,
          signal: this.__abortController?.signal,
          enable_web_search: this.getWebSearchEnabled()
        };
        if (await n(c), u.length === 0) {
          r = [];
          continue;
        }
        r = [
          {
            role: "user",
            type: "tool_response",
            tool_responses: await Promise.all(u),
            timestamp: w()
          }
        ];
      }
      if (s === 0)
        throw new Error("No messages received from the server. Please check your connection.");
      this.setStatus("ready");
      const i = this.__queuedMessages.get();
      if (i.length > 0) {
        this.__queuedMessages.set([]);
        const u = i.filter((_) => _.type === "text" && "text" in _), c = [...i.filter((_) => _.type !== "text")];
        if (u.length > 0) {
          const _ = u.map((p) => "text" in p ? p.text : "").join(`

`);
          c.push({
            role: "user",
            type: "text",
            text: _,
            timestamp: w()
          });
        }
        l = c;
      }
    } catch (r) {
      if (this.setStatus("receiving"), this.setStatus("ready"), z(r))
        return;
      throw r;
    } finally {
      this.__abortController = null;
    }
    l != null && this.sendMessage(l).catch(() => {
    });
  }
  /**
   * Loads the session data (messages and meta)
   */
  async loadSession() {
    const e = window.__hydra_copilot__?.getSession;
    if (e == null)
      throw new Error("Copilot session not configured. Please set the Copilot configuration first.");
    if (this.id == null)
      throw new Error("Session not started");
    return await e(this.id);
  }
  /**
   * Waits for the session to be ready
   */
  async waitUntilReady() {
    if (this.status !== "ready") {
      if (this.status === "idle") {
        await this.initialize();
        return;
      }
      if (this.status === "initializing")
        return new Promise((e, t) => {
          let n = null, a = null;
          const l = () => {
            n !== null && (clearTimeout(n), n = null), a !== null && (a(), a = null);
          };
          a = this.__status.subscribe((r) => {
            r === "ready" ? (l(), e()) : r === "error" ? (l(), t(new Error("Session initialization failed"))) : r !== "initializing" && (l(), t(new Error(`Unexpected session status: ${r}`)));
          }), n = setTimeout(() => {
            l(), t(new Error("Session initialization timeout - exceeded 30 seconds"));
          }, 3e4);
        });
      throw this.status === "error" ? new Error("Session is in error state") : new Error(`Cannot wait for session in "${this.status}" state`);
    }
  }
  /**
   * Gets upload signature for file upload
   * Note: Session readiness is now checked at the upload level (uploadCopilotFile)
   */
  async getUploadSignature(e) {
    if (!this.id)
      throw new Error("Session ID not available - ensure session is ready before calling getUploadSignature");
    const t = window.__hydra_copilot__;
    if (!t?.getUploadSignature)
      throw new Error("File upload not configured. Please provide getUploadSignature in setCopilotConfig.");
    return await t.getUploadSignature({
      id: this.id,
      size: e.size,
      mime_type: e.mime_type
    });
  }
  /**
   * Gets download URL for a previously uploaded file
   */
  async getFileDownloadUrl(e) {
    if (!this.id)
      throw new Error("Session ID not available - ensure session is ready before calling getFileDownloadUrl");
    const t = window.__hydra_copilot__;
    if (!t?.getFileDownloadUrl)
      throw new Error("File download not configured. Please provide getFileDownloadUrl in setCopilotConfig.");
    return (await t.getFileDownloadUrl({
      id: this.id,
      filename: e
    })).url;
  }
  /**
   * Add files to the upload queue
   */
  addUploadFiles(e) {
    const t = this.__uploadFiles.get();
    this.__uploadFiles.set([...t, ...e]);
  }
  /**
   * Update a specific upload file
   */
  updateUploadFile(e, t) {
    const a = this.__uploadFiles.get().map((l) => l.id === e ? { ...l, ...t } : l);
    this.__uploadFiles.set(a);
  }
  /**
   * Remove upload file
   */
  removeUploadFile(e) {
    const n = this.__uploadFiles.get().filter((a) => a.id !== e);
    this.__uploadFiles.set(n);
  }
  /**
   * Clear all upload files
   */
  clearUploadFiles() {
    this.__uploadFiles.set([]);
  }
}
function M(o) {
  return typeof o == "string" ? o : typeof o == "number" || typeof o == "boolean" ? String(o) : Array.isArray(o) || typeof o == "object" ? JSON.stringify(o) : String(o);
}
function U(o, e) {
  const t = [];
  let n = [];
  const a = {}, l = o.filter(
    (s) => {
      if (s.role === "user" && s.type === "tool_response") {
        for (const i of s.tool_responses)
          i.tool_call_id != null && (a[i.tool_call_id] == null && (a[i.tool_call_id] = []), a[i.tool_call_id].push(i));
        return !1;
      }
      return s.type === "text" || s.type === "content" || s.type === "tool_call" || s.type === "meta" || s.type === "error" || s.type === "compaction_start" || s.type === "compaction_complete";
    }
  );
  function r() {
    const s = n.filter((p) => p.type === "text"), i = n.find((p) => p.type === "meta" && "meta" in p);
    if (n = [], s.length === 0)
      return;
    const u = s.map((p) => p.text).join(""), h = i?.meta ?? {}, c = s[0]?.thought;
    let _;
    if (c === !0) {
      const p = t[t.length - 1];
      p != null && p.timestamp != null ? _ = Math.max(0, s[s.length - 1].timestamp - p.timestamp) : _ = 0;
    }
    t.push({
      role: "model",
      type: "text",
      text: u,
      timestamp: s[0].timestamp,
      meta: h,
      ...c === !0 && { thought: c },
      ..._ != null && { duration: _ }
    }), n = [];
  }
  for (const s of l)
    if (s.role === "model" && s.type === "text") {
      const i = n.some((c) => c.type === "text"), u = s.thought === !0, h = n.some(
        (c) => c.type === "text" && "thought" in c && c.thought === !0
      );
      i && (u && !h || !u && h) && r(), n.push(s);
    } else if (s.role === "user" && (s.type === "text" || s.type === "content"))
      r(), t.push(s);
    else if (s.role === "model" && s.type === "meta")
      n.push(s), r();
    else if (s.role === "model" && s.type === "tool_call")
      r(), t.push({
        role: "model",
        type: "tool_call",
        tool_calls: s.tool_calls.map((i) => {
          const u = a[i.id], h = e[i.name];
          if (u == null || u.length === 0)
            return {
              ...i,
              status: "executing"
            };
          const c = u.find((d) => d.response.error != null && d.response.error !== ""), _ = u.some((d) => d.finished), p = u.some((d) => d.was_approved === !0);
          if (c != null)
            return {
              ...i,
              status: "error",
              response: c.response,
              ...p && { was_approved: !0 }
            };
          const g = h?.type === "server" && h?.isStreaming === !0;
          return _ && u.length === 1 && !g ? {
            ...i,
            status: "success",
            response: u[0].response,
            ...p && { was_approved: !0 }
          } : {
            ...i,
            status: _ ? "success" : "executing",
            response: {
              result: u.map((d) => d.response.result),
              error: null
            },
            ...p && { was_approved: !0 }
          };
        }),
        timestamp: s.timestamp
      });
    else if (s.role === "model" && s.type === "compaction_start")
      r(), t.push({
        role: "model",
        type: "compaction",
        status: "compacting",
        timestamp: s.timestamp
      });
    else if (s.role === "model" && s.type === "compaction_complete") {
      r();
      const i = t.findIndex(
        (h) => h.role === "model" && h.type === "compaction" && h.status === "compacting"
      ), u = {
        role: "model",
        type: "compaction",
        status: "complete",
        summary: s.text,
        timestamp: s.timestamp
      };
      i >= 0 ? t[i] = u : t.push(u);
    } else
      s.role === "model" && s.type === "error" ? (r(), t.push(s)) : console.error("Unknown message type", s);
  r();
  for (let s = 0; s < t.length; s++) {
    const i = t[s];
    if (i.role !== "model" || i.type !== "tool_call" || !i.tool_calls.some((c) => c.status === "executing"))
      continue;
    t.slice(s + 1).some((c) => c.role === "model" && (c.type === "text" || c.type === "tool_call" || c.type === "error")) && (t[s] = {
      ...i,
      tool_calls: i.tool_calls.map((c) => c.status === "executing" ? { ...c, status: "success" } : c)
    });
  }
  return t;
}
function A(o) {
  return Object.values(o).filter((e) => e !== void 0 && e.type === "client").map((e) => ({
    name: e.name,
    description: typeof e.description == "string" ? b(e.description) : e.description,
    request_json_schema: e.requestSchema,
    json_response_schema: e.responseSchema
  }));
}
function R(o) {
  return Array.isArray(o) ? o.map((e) => b(e.instruction).trim()) : Object.values(o).filter((e) => e !== void 0).map((e) => b(e.instruction).trim());
}
function q(o) {
  return Array.isArray(o) ? o.map((e) => ({
    description: b(e.description),
    value: M(typeof e.value == "function" ? e.value() : e.value)
  })) : Object.values(o).filter((e) => e !== void 0).map((e) => ({
    description: b(e.description),
    value: M(typeof e.value == "function" ? e.value() : e.value)
  }));
}
function b(o) {
  const e = o.split(`
`), t = e.filter((r) => r.trim().length > 0);
  if (t.length === 0)
    return o;
  const n = t.map((r) => {
    const s = r.match(/^(\s*)/);
    return s ? s[1].length : 0;
  }), a = Math.min(...n);
  return e.map((r) => r.trim().length === 0 ? r : r.slice(a)).join(`
`);
}
function P(o, e) {
  for (const t of o)
    if (t.role === "model" && t.type === "tool_call") {
      const n = t.tool_calls.find((a) => a.id === e);
      if (n)
        return n;
    }
}
function z(o) {
  return o instanceof DOMException && o.name === "AbortError" || o != null && typeof o == "object" && "name" in o && o.name === "CanceledError";
}
function O(o) {
  return {
    role: "user",
    type: "tool_response",
    tool_responses: o.map((e) => ({
      tool_call_id: e.id,
      name: e.name,
      response: { result: { status: "rejected" }, error: null },
      finished: !0
    })),
    timestamp: w()
  };
}
function C(o) {
  const e = {};
  for (const t of o)
    e[t.name] = {
      type: "server",
      name: t.name,
      description: t.description,
      requestSchema: t.request_json_schema,
      responseSchema: t.response_json_schema
    };
  return e;
}
export {
  G as CopilotSession,
  P as findToolCallById,
  q as prepareContextForTransport,
  R as prepareInstructionsForTransport,
  A as prepareToolsForTransport,
  C as serverSideToolsToCopilotTools,
  M as stringifyForCopilot,
  b as undent,
  j as updatedStatus
};
